"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sparkles, Clock3, CalendarDays, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Thursday & Weekend Surge Concentration",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage extreme weekend and Thursday Sai Darshan surges against quieter midweek windows. We configure dynamic minimum length-of-stay (MLOS) rules, buffer management, and early-bird allocation to secure steady RevPAR throughout the week.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic MLOS & midweek stay-extension packages"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Combat intraday rate slashes from competing properties near the temple corridor on MakeMyTrip, Agoda, and Booking.com. Automated competitive tracking protects your ADR without panic price drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "High OTA Commission & Group Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop margin erosion from hefty 18–25% OTA commissions and unauthorized tour-operator discounting. We optimize your direct booking engine, temple transfer bundles, and corporate pilgrim tiers.",
  metricLabel: "Strategy focus",
  metricValue: "Direct pilgrim incentives & verified B2B rate controls"
}];
const seasonalityInsights = [{
  season: "Peak Darshan & Festival Peaks",
  period: "Oct – Dec & Major Utsavs",
  detail: "Vijayadashami (Punyatithi), Guru Purnima, Ram Navami, and Diwali witness 100% market compression. Yield rules capture maximum ADR premium.",
  multiplier: "Max ADR Yield"
}, {
  season: "Weekly Rhythm & Thursday Spikes",
  period: "Year-Round (Thu – Sun)",
  detail: "Heavy pilgrim influx every Thursday through Sunday. Dynamic pacing enforces 2-night minimums and premium room type upsells.",
  multiplier: "High Demand Velocity"
}, {
  season: "Midweek Value & Corporate/Group Base",
  period: "Mon – Wed",
  detail: "Leisure dip offset by family group tiers, direct temple-tour bundles, and senior-citizen packages to sustain baseline occupancy.",
  multiplier: "Occupancy Driver"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeshirdi" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Shirdi
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Shirdi is a high-velocity pilgrimage destination marked by hyper-concentrated Thursday and weekend footfalls, extreme festive compression during Guru Purnima and Vijayadashami, and intense rate competition near the temple vicinity. Sunil Singh implements disciplined, data-backed revenue strategies engineered specifically for Shirdi hotels, luxury pilgrim lodges, and boutique resorts.
          </p>
        </div>

        {/* Seasonality Block */}
        <div className="mb-14 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-secondary">
                <Sparkles className="size-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Shirdi Seasonality Matrix</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mt-1">
                Pilgrimage Demand Curves & Seasonal Optimization
              </h3>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-md self-start md:self-auto">
              <Clock3 className="size-3.5 text-secondary" />
              <span>Real-Time Calendar Yielding</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {seasonalityInsights.map((item, index) => <div key={item.season} data-index={index} className="flex flex-col justify-between rounded-lg border border-border/70 bg-background/60 p-5 hover:border-secondary/40 transition-[border-color,box-shadow] duration-200">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Badge variant="secondary" className="text-[11px] font-mono">
                      {item.period}
                    </Badge>
                    <span className="text-[11px] font-semibold text-secondary flex items-center gap-1">
                      <TrendingUp className="size-3" />
                      {item.multiplier}
                    </span>
                  </div>
                  <h4 className="font-serif text-base font-bold text-foreground mt-2 mb-2">
                    {item.season}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Shirdi hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}